import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'
import PropTypes from 'prop-types'

import StepForm from '../../../../../features/components/Forms/StepForm'
import NameStep from './Steps/NameStep'
import IdentityStep from './Steps/IdentityStep'
import AdDetailsStep from './Steps/AdDetailsStep'
import ViewabilityAndBrandSafetyStep from './Steps/ViewabilityAndBrandSafetyStep'

import { usePurifiedFormik } from '../../../../../hooks/formHooks/usePurifiedFormik'
import { useAccessibleTikTokIdentities } from '../../../../../features/hooks/useAccessibleTikTokIdentities'
import useIsInternalAdCreation from '../../../../../features/hooks/internalAdHooks/useIsInternalAdCreation'

import { createAd, clearCreateAd } from '../../../../../modules/actions/ads'
import {
  adCreateLoadingSelector,
  adWasCreatedSelector,
  adCreateErrorSelector
} from '../../../../../modules/selectors/ads'
import { lineItemSelector } from '../../../../../modules/selectors/lineItems'
import { selectedTikTokAdAccountSelector } from '../../../../../modules/selectors/adAccounts'
import { selectedPartnershipRightsHolderIdSelector } from '../../../../../modules/selectors/partnerships'
import { tiktokRightUploadAdDataSelector } from '../../../../../modules/selectors/rights'
import { campaignSelector } from '../../../../../modules/selectors/campaigns'

import { validationSchema } from './validation'
import { getInitialValues } from './fields'
import { transformValuesToBE } from './formatters'

import { AD_TIKTOK_CREATE } from '../../../../../constants/forms'
import { TIKTOK_PLATFORM } from '../../../../../constants/selectLists/platformList'

function AdTikTokCreateForm({ onSuccessSubmit, formProgressItems }) {
  const dispatch = useDispatch()

  const adWasCreated = useSelector(adWasCreatedSelector)
  const rightsHolderId = useSelector(selectedPartnershipRightsHolderIdSelector)
  const { id: lineItemId } = useSelector(lineItemSelector)
  const { id: campaignId } = useSelector(campaignSelector)
  const { campaign: rightUploadCampaignItemData, lineItem: rightUploadAdLineItemData } = useSelector(
    tiktokRightUploadAdDataSelector
  )
  const { id: selectedAdAccountId } = useSelector(selectedTikTokAdAccountSelector)

  const { id: rightUploadAdCampaignId } = rightUploadCampaignItemData
  const { id: rightUploadAdLineItemId } = rightUploadAdLineItemData

  const { showWarningMessage, identitiesIsLoading, TikTokIdentitiesWarningMessage } = useAccessibleTikTokIdentities()

  const isInternalAdCreation = useIsInternalAdCreation()

  const clearCreateAdHandler = useCallback(() => {
    dispatch(clearCreateAd())
  }, [dispatch])

  const onSubmit = useCallback(
    values => {
      const formattedData = transformValuesToBE({
        values,
        account: selectedAdAccountId,
        // if right upload campaign id exists, use it instead of campaign id from usual selector
        campaignId: rightUploadAdCampaignId ? rightUploadAdCampaignId : campaignId,
        lineItemId: rightUploadAdLineItemId ? rightUploadAdLineItemId : lineItemId,
        isInternalAdCreation,
        rightsHolderId
      })

      dispatch(createAd(formattedData, TIKTOK_PLATFORM))
    },
    [
      dispatch,
      selectedAdAccountId,
      rightUploadAdLineItemId,
      rightUploadAdCampaignId,
      campaignId,
      lineItemId,
      isInternalAdCreation,
      rightsHolderId
    ]
  )

  const initialValues = useMemo(() => getInitialValues(isInternalAdCreation), [isInternalAdCreation])
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  })
  const purifiedFormik = usePurifiedFormik(formik)

  const steps = useMemo(
    () => [
      {
        component: <NameStep />,
        show: true
      },
      {
        component: <IdentityStep />,
        show: true
      },
      {
        component: <AdDetailsStep />,
        show: true
      },
      {
        component: <ViewabilityAndBrandSafetyStep />,
        show: true
      }
    ],
    []
  )

  if (showWarningMessage) {
    return TikTokIdentitiesWarningMessage
  }

  return (
    <StepForm
      steps={steps}
      formik={purifiedFormik}
      formName={AD_TIKTOK_CREATE}
      onSuccessSubmit={onSuccessSubmit}
      formProgressItems={formProgressItems}
      isInitialDataLoading={identitiesIsLoading}
      // processing
      isLoadingSelector={adCreateLoadingSelector}
      successSubmit={adWasCreated}
      errorSelector={adCreateErrorSelector}
      // after form submit
      clearSubmitHandler={clearCreateAdHandler}
    />
  )
}

AdTikTokCreateForm.propTypes = {
  onSuccessSubmit: PropTypes.func,
  formProgressItems: PropTypes.array
}

export default AdTikTokCreateForm
